/**
 * Gross-to-net bridge derived from trade spend stored on customer hierarchy nodes.
 *
 * The Confido importer writes `metadata.trade_spend` on each customer node (see
 * `server/src/integration/confido/`). It lives in metadata rather than as data points because
 * hierarchy aggregation overwrites a parent's `sales_data` from its children — trade spend
 * would be wiped on the first rollup, and would double-count into the revenue totals.
 *
 * All money arrives as fixed 2-decimal strings. Arithmetic is done in whole cents so a bridge
 * that ought to tie out does, instead of drifting by a fraction of a cent per customer.
 */

export interface TradeSpendMetadata {
  revenue?: Record<string, string>
  spend?: Record<string, string>
  retailer_spend?: Record<string, string>
  distributor_spend?: Record<string, string>
  net_sales?: Record<string, string>
  spend_by_reason?: Record<string, Record<string, string>>
}

export interface GrossToNetCustomerRow {
  customer: string
  revenue: number
  retailer_spend: number
  distributor_spend: number
  spend: number
  net_sales: number
  /** Spend ÷ revenue, as a percentage. `null` when the customer plans no revenue. */
  trade_rate_pct: number | null
}

export interface GrossToNetReasonRow {
  reason: string
  spend: number
  /** Share of total spend, as a percentage. */
  share_pct: number
}

export interface GrossToNetBridge {
  revenue: number
  retailer_spend: number
  distributor_spend: number
  spend: number
  net_sales: number
  trade_rate_pct: number | null
  customers: GrossToNetCustomerRow[]
  reasons: GrossToNetReasonRow[]
  /** Periods covered, ascending — used for the panel's subtitle. */
  periods: string[]
}

function to_cents(value: string | number | undefined | null): number {
  if (value == null) return 0
  const n = typeof value === "number" ? value : parseFloat(value)
  return Number.isFinite(n) ? Math.round(n * 100) : 0
}

function sum_cents(values: Record<string, string> | undefined, periods?: Set<string>): number {
  if (!values) return 0
  let total = 0
  for (const [period, value] of Object.entries(values)) {
    if (periods && !periods.has(period)) continue
    total += to_cents(value)
  }
  return total
}

function rate_pct(spend_cents: number, revenue_cents: number): number | null {
  if (revenue_cents === 0) return null
  return (spend_cents / revenue_cents) * 100
}

/** Pull `metadata.trade_spend` off a node, or `null` when the node carries none. */
export function read_trade_spend(node: unknown): TradeSpendMetadata | null {
  const metadata = (node as { metadata?: unknown } | null)?.metadata
  if (!metadata || typeof metadata !== "object") return null
  const trade_spend = (metadata as { trade_spend?: unknown }).trade_spend
  if (!trade_spend || typeof trade_spend !== "object") return null
  return trade_spend as TradeSpendMetadata
}

/**
 * Walk the hierarchy for customer nodes carrying trade spend and build the bridge.
 *
 * `periods` restricts the roll-up to the columns currently on screen, so the bridge always
 * describes the same window as the grid above it. Omit it to take everything stored.
 */
export function build_gross_to_net(
  roots: unknown[],
  periods?: string[],
  /**
   * Revenue the grid shows for a customer node right now (scenario and unsaved edits applied), by
   * node id, in dollars. When given for a node, it replaces the planned revenue in
   * `metadata.trade_spend` and the planned spend is scaled to it at the customer's planned trade
   * rate, so Net Revenue and the bridge move with Baseline / Weighted / Full Upside and with grid
   * edits the way Total Annual Forecast does. Nodes it does not cover keep their stored figures.
   */
  live_revenue_by_node?: Map<string, number>
): GrossToNetBridge | null {
  const period_filter = periods && periods.length > 0 ? new Set(periods) : undefined
  // Keyed by customer name: the same account under two parents merges into one row
  // (duplicate names collided as list keys and read as a double count).
  const customer_cents = new Map<
    string,
    { revenue: number; retailer_spend: number; distributor_spend: number; spend: number }
  >()
  const reason_cents = new Map<string, number>()
  const seen_periods = new Set<string>()

  function walk(node: any): void {
    const trade_spend = read_trade_spend(node)
    if (trade_spend) {
      const planned_revenue = sum_cents(trade_spend.revenue, period_filter)
      const live_dollars = live_revenue_by_node?.get(String(node?.id ?? ""))
      const revenue = live_dollars != null ? Math.round(live_dollars * 100) : planned_revenue
      // Spend follows revenue at the planned trade rate; with no planned revenue there is no rate,
      // so the planned spend stands as is.
      const scale = live_dollars != null && planned_revenue !== 0 ? revenue / planned_revenue : 1
      const rescale = (cents: number): number => Math.round(cents * scale)
      const retailer_spend = rescale(sum_cents(trade_spend.retailer_spend, period_filter))
      const distributor_spend = rescale(sum_cents(trade_spend.distributor_spend, period_filter))
      // Older imports stored only the combined figure; fall back rather than showing zeros.
      const combined = rescale(sum_cents(trade_spend.spend, period_filter))
      const spend = retailer_spend + distributor_spend || combined

      // A customer with neither revenue nor spend in view is noise on the bridge.
      if (revenue !== 0 || spend !== 0) {
        const name = String(node?.name ?? "Unknown")
        const acc = customer_cents.get(name) ?? { revenue: 0, retailer_spend: 0, distributor_spend: 0, spend: 0 }
        acc.revenue += revenue
        acc.retailer_spend += retailer_spend
        acc.distributor_spend += distributor_spend
        acc.spend += spend
        customer_cents.set(name, acc)

        for (const [reason, by_period] of Object.entries(trade_spend.spend_by_reason ?? {})) {
          const cents = rescale(sum_cents(by_period, period_filter))
          if (cents !== 0) {
            reason_cents.set(reason, (reason_cents.get(reason) ?? 0) + cents)
          }
        }
        for (const period of Object.keys(trade_spend.revenue ?? {})) {
          if (!period_filter || period_filter.has(period)) seen_periods.add(period)
        }
      }
    }
    for (const child of (node?.children ?? []) as unknown[]) {
      walk(child)
    }
  }

  for (const root of roots) walk(root)
  if (customer_cents.size === 0) return null

  const customers: GrossToNetCustomerRow[] = [...customer_cents.entries()].map(([name, acc]) => ({
    customer: name,
    revenue: acc.revenue / 100,
    retailer_spend: acc.retailer_spend / 100,
    distributor_spend: acc.distributor_spend / 100,
    spend: acc.spend / 100,
    net_sales: (acc.revenue - acc.spend) / 100,
    trade_rate_pct: rate_pct(acc.spend, acc.revenue),
  }))

  const revenue_cents = customers.reduce((sum, c) => sum + Math.round(c.revenue * 100), 0)
  const retailer_cents = customers.reduce((sum, c) => sum + Math.round(c.retailer_spend * 100), 0)
  const distributor_cents = customers.reduce((sum, c) => sum + Math.round(c.distributor_spend * 100), 0)
  const spend_cents = customers.reduce((sum, c) => sum + Math.round(c.spend * 100), 0)

  customers.sort((a, b) => b.revenue - a.revenue || a.customer.localeCompare(b.customer))

  const reasons: GrossToNetReasonRow[] = [...reason_cents.entries()]
    .map(([reason, cents]) => ({
      reason,
      spend: cents / 100,
      share_pct: spend_cents === 0 ? 0 : (cents / spend_cents) * 100,
    }))
    .sort((a, b) => b.spend - a.spend || a.reason.localeCompare(b.reason))

  return {
    revenue: revenue_cents / 100,
    retailer_spend: retailer_cents / 100,
    distributor_spend: distributor_cents / 100,
    spend: spend_cents / 100,
    net_sales: (revenue_cents - spend_cents) / 100,
    trade_rate_pct: rate_pct(spend_cents, revenue_cents),
    customers,
    reasons,
    periods: [...seen_periods].sort(),
  }
}

function cents_to_string(cents: number): string {
  return (cents / 100).toFixed(2)
}

/**
 * Rewrite a customer's trade spend so the periods in view hit `target_rate_pct` of revenue.
 *
 * Per period: the target spend is revenue × rate; the existing funder split and per-reason
 * mix are preserved proportionally. A period with no existing spend books the whole amount
 * as retailer-funded under "Manual adjustment". Zero-revenue periods and periods outside
 * `periods` are left untouched. Returns a new object — callers store it back into
 * `metadata.trade_spend`, which round-trips through the normal hierarchy save.
 */
export function apply_trade_rate_to_customer(
  trade_spend: TradeSpendMetadata,
  target_rate_pct: number,
  periods?: string[]
): TradeSpendMetadata {
  const period_filter = periods && periods.length > 0 ? new Set(periods) : undefined
  const next: TradeSpendMetadata = structuredClone(trade_spend)
  next.retailer_spend = next.retailer_spend ?? {}
  next.spend = next.spend ?? {}
  next.net_sales = next.net_sales ?? {}

  for (const [period, revenue_str] of Object.entries(trade_spend.revenue ?? {})) {
    if (period_filter && !period_filter.has(period)) continue
    const revenue_cents = to_cents(revenue_str)
    if (revenue_cents === 0) continue

    const target_cents = Math.round((revenue_cents * target_rate_pct) / 100)
    const existing_retailer = to_cents(trade_spend.retailer_spend?.[period])
    const existing_distributor = to_cents(trade_spend.distributor_spend?.[period])
    const existing_cents = existing_retailer + existing_distributor || to_cents(trade_spend.spend?.[period])

    if (existing_cents > 0) {
      const factor = target_cents / existing_cents
      // Round the retailer share; give the distributor the remainder so the split ties out.
      const next_retailer = Math.round(existing_retailer * factor)
      const next_distributor = existing_retailer + existing_distributor > 0 ? target_cents - next_retailer : 0
      next.retailer_spend[period] = cents_to_string(
        existing_retailer + existing_distributor > 0 ? next_retailer : target_cents
      )
      if (trade_spend.distributor_spend?.[period] != null) {
        next.distributor_spend = next.distributor_spend ?? {}
        next.distributor_spend[period] = cents_to_string(next_distributor)
      }
      for (const [reason, by_period] of Object.entries(trade_spend.spend_by_reason ?? {})) {
        const reason_period_cents = to_cents(by_period?.[period])
        if (reason_period_cents !== 0) {
          next.spend_by_reason = next.spend_by_reason ?? {}
          next.spend_by_reason[reason] = { ...(next.spend_by_reason[reason] ?? {}) }
          next.spend_by_reason[reason][period] = cents_to_string(Math.round(reason_period_cents * factor))
        }
      }
    } else if (target_cents !== 0) {
      next.retailer_spend[period] = cents_to_string(target_cents)
      next.spend_by_reason = next.spend_by_reason ?? {}
      next.spend_by_reason["Manual adjustment"] = {
        ...(next.spend_by_reason["Manual adjustment"] ?? {}),
        [period]: cents_to_string(target_cents),
      }
    } else {
      continue
    }

    next.spend[period] = cents_to_string(target_cents)
    next.net_sales[period] = cents_to_string(revenue_cents - target_cents)
  }

  return next
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  const customer = (name: string, trade_spend: TradeSpendMetadata | null, children: unknown[] = []) => ({
    name,
    level: "customer",
    metadata: trade_spend ? { source: "confido", trade_spend } : { source: "confido" },
    children,
  })

  describe("build_gross_to_net with live revenue", () => {
    const stored: TradeSpendMetadata = {
      revenue: { "2027-01": "1000.00" },
      retailer_spend: { "2027-01": "150.00" },
      distributor_spend: { "2027-01": "50.00" },
      spend: { "2027-01": "200.00" },
      spend_by_reason: { "Retailer - Scans": { "2027-01": "150.00" }, "Distributor - OI": { "2027-01": "50.00" } },
    }

    it("follows the scenario's revenue and keeps the planned trade rate", () => {
      const node = { ...customer("Whole Foods", stored), id: "n1" }
      // Full Upside lifts Whole Foods to $1,500: spend scales 20% → $300, net $1,200.
      const bridge = build_gross_to_net([node], ["2027-01"], new Map([["n1", 1500]]))
      expect(bridge?.revenue).toBe(1500)
      expect(bridge?.spend).toBe(300)
      expect(bridge?.net_sales).toBe(1200)
      expect(bridge?.trade_rate_pct).toBeCloseTo(20)
      expect(bridge?.customers[0]).toMatchObject({ retailer_spend: 225, distributor_spend: 75 })
      expect(bridge?.reasons.find((r) => r.reason === "Retailer - Scans")?.spend).toBe(225)
    })

    it("leaves a node the map does not cover on its stored figures", () => {
      const node = { ...customer("Whole Foods", stored), id: "n1" }
      const bridge = build_gross_to_net([node], ["2027-01"], new Map([["other", 1500]]))
      expect(bridge?.revenue).toBe(1000)
      expect(bridge?.spend).toBe(200)
    })

    it("keeps planned spend when the plan had no revenue to rate it against", () => {
      const node = { ...customer("Sprouts", { revenue: { "2027-01": "0.00" }, retailer_spend: { "2027-01": "40.00" } }), id: "n2" }
      const bridge = build_gross_to_net([node], ["2027-01"], new Map([["n2", 500]]))
      expect(bridge?.revenue).toBe(500)
      expect(bridge?.spend).toBe(40)
    })
  })

  describe("apply_trade_rate_to_customer", () => {
    it("rescales spend, funder split, and reasons to hit the target rate", () => {
      const next = apply_trade_rate_to_customer(
        {
          revenue: { "2027-01": "1000.00" },
          retailer_spend: { "2027-01": "150.00" },
          distributor_spend: { "2027-01": "50.00" },
          spend: { "2027-01": "200.00" },
          net_sales: { "2027-01": "800.00" },
          spend_by_reason: { "Retailer - Scans": { "2027-01": "150.00" }, "Distributor - OI": { "2027-01": "50.00" } },
        },
        30
      )
      // 30% of $1,000 = $300, split 3:1 like the existing spend.
      expect(next.retailer_spend?.["2027-01"]).toBe("225.00")
      expect(next.distributor_spend?.["2027-01"]).toBe("75.00")
      expect(next.spend?.["2027-01"]).toBe("300.00")
      expect(next.net_sales?.["2027-01"]).toBe("700.00")
      expect(next.spend_by_reason?.["Retailer - Scans"]?.["2027-01"]).toBe("225.00")
    })

    it("books new spend as retailer-funded manual adjustment when none existed", () => {
      const next = apply_trade_rate_to_customer({ revenue: { "2027-01": "1000.00" } }, 10)
      expect(next.retailer_spend?.["2027-01"]).toBe("100.00")
      expect(next.spend?.["2027-01"]).toBe("100.00")
      expect(next.spend_by_reason?.["Manual adjustment"]?.["2027-01"]).toBe("100.00")
    })

    it("only touches the periods in view and leaves zero-revenue periods alone", () => {
      const next = apply_trade_rate_to_customer(
        {
          revenue: { "2027-01": "1000.00", "2027-02": "1000.00", "2027-03": "0.00" },
          retailer_spend: { "2027-01": "100.00", "2027-02": "100.00", "2027-03": "40.00" },
        },
        50,
        ["2027-01", "2027-03"]
      )
      expect(next.retailer_spend?.["2027-01"]).toBe("500.00")
      // Feb is off screen — untouched.
      expect(next.retailer_spend?.["2027-02"]).toBe("100.00")
      // March plans no revenue, so a rate cannot produce a spend figure — untouched.
      expect(next.retailer_spend?.["2027-03"]).toBe("40.00")
    })

    it("does not mutate the input", () => {
      const original: TradeSpendMetadata = {
        revenue: { "2027-01": "1000.00" },
        retailer_spend: { "2027-01": "100.00" },
      }
      apply_trade_rate_to_customer(original, 50)
      expect(original.retailer_spend?.["2027-01"]).toBe("100.00")
    })
  })

  describe("build_gross_to_net", () => {
    it("returns null when no node carries trade spend", () => {
      expect(build_gross_to_net([customer("KeHE", null)])).toBeNull()
      expect(build_gross_to_net([])).toBeNull()
    })

    it("bridges revenue to net sales and splits the two funders", () => {
      const bridge = build_gross_to_net([
        customer("KeHE", {
          revenue: { "2027-01": "1000.00", "2027-02": "1000.00" },
          retailer_spend: { "2027-01": "250.00" },
          distributor_spend: { "2027-02": "50.00" },
          spend: { "2027-01": "250.00", "2027-02": "50.00" },
          spend_by_reason: {
            "Retailer - Scans": { "2027-01": "250.00" },
            "Distributor - Off-Invoice Discount": { "2027-02": "50.00" },
          },
        }),
      ])!

      expect(bridge.revenue).toBe(2000)
      expect(bridge.retailer_spend).toBe(250)
      expect(bridge.distributor_spend).toBe(50)
      expect(bridge.spend).toBe(300)
      expect(bridge.net_sales).toBe(1700)
      expect(bridge.trade_rate_pct).toBeCloseTo(15, 6)
      expect(bridge.reasons.map((r) => r.reason)).toEqual([
        "Retailer - Scans",
        "Distributor - Off-Invoice Discount",
      ])
      expect(bridge.reasons[0].share_pct).toBeCloseTo(83.333, 3)
    })

    it("restricts the bridge to the periods on screen", () => {
      const node = customer("KeHE", {
        revenue: { "2027-01": "1000.00", "2027-02": "1000.00" },
        retailer_spend: { "2027-01": "250.00", "2027-02": "400.00" },
        spend_by_reason: { "Retailer - Scans": { "2027-01": "250.00", "2027-02": "400.00" } },
      })

      const january = build_gross_to_net([node], ["2027-01"])!
      expect(january.revenue).toBe(1000)
      expect(january.spend).toBe(250)
      expect(january.reasons[0].spend).toBe(250)
      expect(january.periods).toEqual(["2027-01"])
    })

    it("finds customers nested under a parent and sorts by revenue", () => {
      const bridge = build_gross_to_net([
        {
          name: "Root",
          level: "class_of_trade",
          children: [
            customer("KeHE", { revenue: { "2027-01": "100.00" }, retailer_spend: { "2027-01": "10.00" } }),
            customer("UNFI East", { revenue: { "2027-01": "900.00" }, retailer_spend: { "2027-01": "450.00" } }),
          ],
        },
      ])!

      expect(bridge.customers.map((c) => c.customer)).toEqual(["UNFI East", "KeHE"])
      expect(bridge.customers[0].trade_rate_pct).toBeCloseTo(50, 6)
      expect(bridge.revenue).toBe(1000)
    })

    it("drops customers with neither revenue nor spend in view", () => {
      const bridge = build_gross_to_net([
        customer("KeHE", { revenue: { "2027-01": "100.00" }, retailer_spend: { "2027-01": "10.00" } }),
        // Confido exports a row per account, including dormant ones that are all zeros.
        customer("Wakefern", { revenue: { "2027-01": "0.00" }, retailer_spend: { "2027-01": "0.00" } }),
      ])!
      expect(bridge.customers.map((c) => c.customer)).toEqual(["KeHE"])
    })

    it("reports no trade rate for a customer that plans spend against no revenue", () => {
      const bridge = build_gross_to_net([
        customer("Costco", { revenue: { "2027-01": "0.00" }, retailer_spend: { "2027-01": "500.00" } }),
      ])!
      expect(bridge.customers[0].trade_rate_pct).toBeNull()
      expect(bridge.customers[0].net_sales).toBe(-500)
    })

    it("falls back to the combined spend map when the funder split is absent", () => {
      const bridge = build_gross_to_net([
        customer("KeHE", { revenue: { "2027-01": "1000.00" }, spend: { "2027-01": "300.00" } }),
      ])!
      expect(bridge.spend).toBe(300)
      expect(bridge.net_sales).toBe(700)
    })

    it("merges duplicate customer names into one row instead of double-listing", () => {
      // The same account can appear under two parents (two classes of trade); two rows
      // with the same name collide as Preact keys and read as a double count.
      const bridge = build_gross_to_net([
        customer("KeHE", { revenue: { "2027-01": "600.00" }, retailer_spend: { "2027-01": "60.00" } }),
        customer("KeHE", { revenue: { "2027-01": "400.00" }, retailer_spend: { "2027-01": "40.00" } }),
      ])!
      expect(bridge.customers).toHaveLength(1)
      expect(bridge.customers[0].revenue).toBe(1000)
      expect(bridge.customers[0].spend).toBe(100)
      expect(bridge.revenue).toBe(1000)
    })

    it("ties out in whole cents across many customers", () => {
      // Values chosen so naive float addition drifts off the expected total.
      const nodes = Array.from({ length: 100 }, (_, i) =>
        customer(`C${i}`, {
          revenue: { "2027-01": "0.10" },
          retailer_spend: { "2027-01": "0.07" },
        }),
      )
      const bridge = build_gross_to_net(nodes)!
      expect(bridge.revenue).toBe(10)
      expect(bridge.spend).toBe(7)
      expect(bridge.net_sales).toBe(3)
    })
  })
}
